/**
 * API client for leaderboard endpoints.
 */
import { apiUrl } from './config';
import { getAuthToken, getPortalType } from '@/utils/auth';

/**
 * Leaderboard API client
 */
export const leaderboardAPI = {
  /**
   * Get leaderboard for a specific grade
   * @param {number} gradeId - Grade ID
   * @param {string} period - Time period: 'all_time', 'weekly', or 'monthly'
   */
  getLeaderboard: async (gradeId, period = 'all_time') => {
    const portal = getPortalType(window.location.pathname || '');
    const token = getAuthToken(portal);
    
    if (!token) {
      throw new Error('Authentication token not found. Please log in again.');
    }
    
    const baseUrl = apiUrl(`/leaderboard/grade/${gradeId}/`);
    const url = baseUrl.includes('?') 
      ? `${baseUrl}&period=${period}` 
      : `${baseUrl}?period=${period}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
      }
      const errorMessage = errorData.error || errorData.message || errorData.detail || `Failed to fetch leaderboard (${response.status})`;
      console.error('Leaderboard API error:', response.status, errorData);
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  },

  /**
   * Get current student's rank in their grade
   * @param {number} gradeId - Grade ID
   * @param {string} period - Time period: 'all_time', 'weekly', or 'monthly'
   */
  getMyRank: async (gradeId, period = 'all_time') => {
    const portal = getPortalType(window.location.pathname || '');
    const token = getAuthToken(portal);
    const baseUrl = apiUrl(`/leaderboard/grade/${gradeId}/my-rank/`);
    const url = baseUrl.includes('?') 
      ? `${baseUrl}&period=${period}` 
      : `${baseUrl}?period=${period}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
      }
      const errorMessage = errorData.error || errorData.message || errorData.detail || `Failed to fetch rank (${response.status})`;
      console.error('MyRank API error:', response.status, errorData);
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  },
};
